/**
 * QuickAddMenu - right-click a row in the Opportunities or Projects table to
 * add a task, add a comment, or flag the project's status without opening it.
 *
 * Right-click (not a hover button) because the tables are dense and already
 * carry per-row controls; the browser menu is suppressed only over table rows.
 * Escape or a click anywhere closes it.
 *
 * `actions` (10/14/26): more choices under the three, [[key, text, (row) => ...]]
 * - the Projects tab's "Add PO line" / "Add sub-project". Picking one closes
 * the menu and hands the row over.
 */
import { useEffect, useState } from 'react';
import TaskComposer from './TaskComposer';
import { notifyTasksChanged } from './useTasks';
import { notifyCommentsChanged } from '../../hooks/useCommentsIndex';
import { HEALTH, HEALTH_ORDER, healthOf, needsReason } from '../../data/projectHealth';
import { useApiBase } from '../../context/DataScope';

const PANEL = {
  position: 'fixed',
  zIndex: 90,
  background: '#fff',
  border: '1px solid #DDE2DD',
  borderRadius: 10,
  boxShadow: '0 12px 34px rgba(0,0,0,0.18)',
  fontFamily: 'Montserrat',
};

export default function QuickAddMenu({ target, onClose, onAdded, onSetHealth, actions }) {
  const [mode, setMode] = useState(null); // null | 'task' | 'comment' | 'health'
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [pickedHealth, setPickedHealth] = useState(null);
  const [error, setError] = useState('');
  const base = useApiBase(); // the KSA tab swaps in its own prefix (context/DataScope.js)

  /* Right-clicking a second row swaps `target` but does NOT remount this
     component - it is rendered permanently and returns null when idle - so
     whatever was typed, and whichever mode was open, used to carry over: a
     half-written comment reappeared on someone else's project, and the menu
     stayed stuck on Comment with no way back to Add task. Every new target
     starts clean. */
  useEffect(() => {
    setMode(null);
    setComment('');
    setBusy(false);
    setPickedHealth(null);
    setError('');
  }, [target]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!target) return null;
  const { row, x, y } = target;
  const label = row.description || row.name || row.customer || row.id;
  const width = mode ? 340 : 210;
  const left = Math.min(x, window.innerWidth - width - 12);
  const top = Math.min(
    y,
    window.innerHeight -
      (mode === 'health' ? 380 : mode === 'task' ? 290 : mode ? 250 : 140 + (actions && actions.length ? 20 + actions.length * 40 : 0))
  );

  const addTask = async ({ description, owner, due_date, tagged }) => {
    const r = await fetch(`${base}/crm-actions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description, owner, due_date, tagged, entity_type: 'project', entity_id: row.id, entity_label: label }),
    });
    if (r.ok) {
      notifyTasksChanged();
      onAdded('Task added');
      onClose();
    }
    return r.ok;
  };

  const addComment = async () => {
    const body = comment.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${base}/projects/${encodeURIComponent(row.id)}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (r.ok) {
        notifyCommentsChanged();
        onAdded('Comment added');
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  /** Set the flag, and for yellow/red record WHY as a matching comment. */
  const saveHealth = async () => {
    if (!pickedHealth || busy) return;
    const why = comment.trim();
    if (needsReason(pickedHealth) && !why) {
      setError(`A ${HEALTH[pickedHealth].label.toLowerCase()} flag needs a note saying what the issue is.`);
      return;
    }
    setBusy(true);
    try {
      // The note goes first. If the flag saved and the note did not, the colour
      // would sit there unexplained - which is the whole thing being prevented.
      if (why) {
        const c = await fetch(`${base}/projects/${encodeURIComponent(row.id)}/comments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: why, severity: needsReason(pickedHealth) ? pickedHealth : undefined }),
        });
        if (!c.ok) {
          setError('Could not save the note - the status was left unchanged.');
          return;
        }
        notifyCommentsChanged();
      }
      const ok = await onSetHealth(row, pickedHealth === 'green' ? null : pickedHealth);
      if (ok === false) {
        setError('Could not save the status.');
        return;
      }
      onAdded(`Status set to ${HEALTH[pickedHealth].label}`);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const CHOICES = [['task', '＋  Add task'], ['comment', '💬  Add comment'], ...(onSetHealth ? [['health', '🚦  Flag status']] : [])];

  return (
    <>
      {/* click-away */}
      <div
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
        style={{ position: 'fixed', inset: 0, zIndex: 89 }}
      />
      <div style={{ ...PANEL, left, top, width }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '9px 12px', borderBottom: '1px solid #EFEFEF', display: 'flex', alignItems: 'center', gap: 7 }}>
          {/* Picking one used to be a one-way door - the only way back was to
              close the menu and right-click again. */}
          {mode && (
            <button
              type="button"
              title="Back"
              aria-label="Back"
              onClick={() => {
                setMode(null);
                setComment('');
                setPickedHealth(null);
                setError('');
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 800,
                color: '#1B5E20',
                fontFamily: 'Montserrat',
                flexShrink: 0,
              }}
            >
              ←
            </button>
          )}
          <span
            style={{ fontSize: 10, color: '#8A9289', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {label}
          </span>
        </div>

        {!mode && (
          <div style={{ padding: 5 }}>
            {CHOICES.map(([m, text]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '9px 11px',
                  border: 'none',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#0D0D0D',
                  fontFamily: 'Montserrat',
                  borderRadius: 6,
                }}
              >
                {text}
              </button>
            ))}
            {actions && actions.length > 0 && (
              <>
                <div style={{ height: 1, background: '#EFEFEF', margin: '4px 6px' }} />
                {actions.map(([key, text, run]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      onClose();
                      run(row);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '9px 11px',
                      border: 'none',
                      background: '#fff',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#1B5E20',
                      fontFamily: 'Montserrat',
                      borderRadius: 6,
                    }}
                  >
                    {text}
                  </button>
                ))}
              </>
            )}
          </div>
        )}

        {mode === 'task' && (
          <div style={{ padding: 11 }}>
            <TaskComposer compact autoFocus onSubmit={addTask} onCancel={onClose} />
          </div>
        )}

        {mode === 'health' && (
          <div style={{ padding: 11, display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {HEALTH_ORDER.map((k) => {
                const h = HEALTH[k];
                const active = (pickedHealth || healthOf(row)) === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => {
                      setPickedHealth(k);
                      setError('');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 10px',
                      borderRadius: 7,
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'Montserrat',
                      border: `1.5px solid ${active ? h.dot : '#E6E6E6'}`,
                      background: active ? h.bg : '#fff',
                    }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: h.dot, flexShrink: 0 }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: h.fg }}>{h.label}</span>
                      <span style={{ display: 'block', fontSize: 9.5, color: '#8A9289' }}>{h.blurb}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Yellow and red cannot be set silently: a colour with no reason
                tells the next person there is a problem but not what it is. */}
            {needsReason(pickedHealth) && (
              <textarea
                autoFocus
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  setError('');
                }}
                placeholder={pickedHealth === 'red' ? 'What needs immediate action?' : 'What should people be aware of?'}
                style={{
                  padding: '7px 9px',
                  borderRadius: 6,
                  border: `1px solid ${HEALTH[pickedHealth].dot}`,
                  fontSize: 11.5,
                  fontFamily: 'Montserrat',
                  minHeight: 62,
                  resize: 'vertical',
                  outline: 'none',
                }}
              />
            )}
            {error && <div style={{ fontSize: 10.5, fontWeight: 700, color: '#B71C1C' }}>{error}</div>}

            <div style={{ display: 'flex', gap: 7 }}>
              <button
                type="button"
                onClick={saveHealth}
                disabled={!pickedHealth || busy}
                style={{
                  padding: '7px 14px',
                  borderRadius: 6,
                  border: 'none',
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: 'Montserrat',
                  cursor: !pickedHealth || busy ? 'not-allowed' : 'pointer',
                  background: !pickedHealth || busy ? '#C8D6C9' : '#1B5E20',
                  color: '#fff',
                }}
              >
                {busy ? 'Saving…' : 'Save status'}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '7px 12px',
                  borderRadius: 6,
                  border: '1px solid #DDD',
                  background: '#fff',
                  color: '#666',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {mode === 'comment' && (
          <div style={{ padding: 11, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <textarea
              autoFocus
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addComment();
              }}
              placeholder="Add a comment…"
              style={{
                padding: '7px 9px',
                borderRadius: 6,
                border: '1px solid #DDD',
                fontSize: 11.5,
                fontFamily: 'Montserrat',
                minHeight: 62,
                resize: 'vertical',
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 7 }}>
              <button
                type="button"
                onClick={addComment}
                disabled={!comment.trim() || busy}
                style={{
                  padding: '7px 14px',
                  borderRadius: 6,
                  border: 'none',
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: 'Montserrat',
                  cursor: !comment.trim() || busy ? 'not-allowed' : 'pointer',
                  background: !comment.trim() || busy ? '#C8D6C9' : '#1B5E20',
                  color: '#fff',
                }}
              >
                {busy ? 'Saving…' : 'Add comment'}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '7px 12px',
                  borderRadius: 6,
                  border: '1px solid #DDD',
                  background: '#fff',
                  color: '#666',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
